//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { crl8, isAuthedSession } from '@app/curalate.globals';
import {
  getIsModerationMergeEnabled,
  getIsModeratorCapable,
} from '@moderation-mode/moderationModeDerivation';

/**
 * Landing state for Moderator View, and the redirect target when a moderator
 * reaches a path outside the allowed surface.
 */
export const MODERATOR_DEFAULT_STATE = 'library.moderation-all-shared-media';
export const MODERATION_STREAM_STATE = 'library.moderation-stream';

/**
 * Paths reachable in Moderator View.
 *
 * - The two library paths are listed in full, not as a shared
 *   `/library/moderation/` prefix: the brand's AI curation routes
 *   (`/distribute`, `/needs-action`, `/rejected`) sit under that same prefix
 *   and must stay blocked.
 * - `/account-settings` and `/settings/account` keep the mode switcher (in My
 *   Account) reachable, so a moderator is never stranded in Moderator View.
 * - The named auth / utility paths let unauthenticated and bootstrap flows run.
 *
 * OAuth provider callbacks are NOT listed here; they are matched structurally
 * by isPathAllowedInModeratorView (any `/<provider>/oauth`), so adding a new
 * provider never silently breaks under Moderator View.
 */
export const MODERATOR_ALLOWED_PATH_PREFIXES = [
  '/library/moderation/all-shared-media',
  '/library/moderation/streams',
  '/insights/moderation',
  '/account-settings',
  '/settings/account',
  '/error',
  '/login-from-bv-portal',
  '/freshdesk-login',
  '/mobile-install',
  '/walmart/login',
];
/**
 * OAuth callbacks are mode-agnostic plumbing — e.g. reconnecting Google
 * Analytics from Account Settings bounces through one before the code is
 * exchanged. Matched by shape (exactly `/<provider>/oauth`) so the set can't
 * drift as providers are added, without letting deeper brand routes that merely
 * end in `/oauth` (e.g. a Creator Details page for username "oauth") slip
 * through.
 */
const isOAuthCallbackPath = (path: string): boolean =>
  /^\/[^/]+\/oauth$/.test(path);

/**
 * Backs `hasAccess` on the moderator states (surface visibility, not action
 * authority). Capability-only, never the active mode, so the states stay
 * resolvable in Client View and before the mode settles. isAuthedSession
 * guards the logged-out session whose client/user reads would otherwise throw.
 */
export const canAccessModeratorView = (): boolean => {
  const session = crl8.session;

  if (!isAuthedSession(session)) {
    return false;
  }

  if (!getIsModerationMergeEnabled(session)) {
    return false;
  }

  return (
    session.internalInfo.isCuralateEmployee ||
    getIsModeratorCapable(session.permissions)
  );
};

/**
 * The real confinement for Moderator View (nav hiding is only cosmetic — deep
 * links, history and typed URLs still reach brand routes).
 */
export const isPathAllowedInModeratorView = (path: string): boolean =>
  isOAuthCallbackPath(path) ||
  MODERATOR_ALLOWED_PATH_PREFIXES.some(
    (allowedPath) => path === allowedPath || path.startsWith(`${allowedPath}/`),
  );
